import { z } from 'zod';

import { CURRENT_API_STAGE } from '../constants/env';
import { type UUID } from '../types/common';
import { calcValue } from './common';
import { removeDiacritics } from './normalizers';
import { encodeBase64 } from './string';

/**
 * Creates URL object from string or URL builder function
 * @param url The URL as string or URL builder function
 * @param basePath The base path to use
 * @returns The URL object
 */
export const createUrlWithBuilder = (url: UrlBuilder, basePath: string | URL) => url(new URL(basePath));
export type UrlBuilder = (url: URL) => URL;

/* -------------------------------------------------------------------------------------------------
 * URL builders
 * -----------------------------------------------------------------------------------------------*/

/**
 * Returns correct path to NFCtron API
 * @param url The URL to append to the base path or a URL builder function
 * @param stage The API stage to use, defaults to the current API stage
 * @returns The an URL object to NFCtron API
 */
export const getApiUrl = (url: string | UrlBuilder = '/', stage: string | undefined = CURRENT_API_STAGE) => {
	const basePath = calcValue(() => {
		switch (stage) {
			case 'production':
				return 'https://api.nfctron.com';
			case 'beta':
				return 'https://api.beta.nfctron.com';
			case 'staging':
			default:
				return 'https://api.staging.nfctron.com';
		}
	});
	return typeof url === 'function' ? createUrlWithBuilder(url, basePath) : new URL(url, basePath);
};

/**
 * Returns correct path to NFCtron Pass app
 * @param url The URL to append to the base path or a URL builder function
 * @param stage The API stage to use, defaults to the current API stage
 * @deprecated You probably want to use `getTicketsUrl` instead. NFCtron Pass is a deprecated service.
 * @returns The an URL object to NFCtron Pass app
 */
export const getPassUrl = (url: string | UrlBuilder = '/', stage: string | undefined = CURRENT_API_STAGE) => {
	const basePath = calcValue(() => {
		switch (stage) {
			case 'production':
			case 'beta':
				return 'https://pass.nfctron.com';
			case 'staging':
			default:
				return 'https://test.pass.nfctron.com';
		}
	});
	return typeof url === 'function' ? createUrlWithBuilder(url, basePath) : new URL(url, basePath);
};

/**
 * Collection of registered NFCtron Tickets routes
 */
export const registeredTicketsRoutes = {
	index: (params: {}) => '/',
	organizerDetail: (params: { organizerSlug: string }) => `/organizer/${params.organizerSlug}`,
	eventGroupDetail: (params: { eventGroupSlug: string }) => `/tour/${params.eventGroupSlug}`,
	eventCart: (params: { organizerSlug: string; eventSlug: string }) => `/event/${params.organizerSlug}/${params.eventSlug}/cart`,
	eventCartDemo: (params: { organizerSlug: string; eventSlug: string; eventId: UUID }) =>
		createUrlWithBuilder((url) => {
			url.searchParams.set('secret', encodeBase64(params.eventId));
			return url;
		}, `/event/${params.organizerSlug}/${params.eventSlug}/cart/demo`).href,
	eventOrderDetail: (params: { organizerSlug: string; eventSlug: string; orderId: UUID; fromQueue?: boolean; enforceMethod?: boolean }) =>
		createUrlWithBuilder((url) => {
			url.searchParams.set('fromQueue', params.fromQueue?.toString() ?? 'false');
			url.searchParams.set('enforceMethod', params.enforceMethod?.toString() ?? 'false');
			return url;
		}, `/event/${params.organizerSlug}/${params.eventSlug}/order/${params.orderId}`).href,
} as const;

/**
 * Returns correct path to NFCtron Tickets app
 * @param url The URL to append to the base path or a URL builder function
 * @param stage The API stage to use, defaults to the current API stage
 * @returns The an URL object to NFCtron Tickets app
 */
export const getTicketsUrl = (url: string | UrlBuilder = '/', stage: string | undefined = CURRENT_API_STAGE) => {
	const basePath = calcValue(() => {
		switch (stage) {
			case 'production':
				return 'https://tickets.nfctron.com';
			case 'beta':
				return 'https://beta.tickets.nfctron.com';
			case 'staging':
			default:
				return 'https://dev.tickets.nfctron.com';
		}
	});
	return typeof url === 'function' ? createUrlWithBuilder(url, basePath) : new URL(url, basePath);
};

/**
 * Collection of registered NFCtron Hub routes
 */
export const registeredHubRoutes = {
	index: (params: {}) => '/',
	// TODO: add query params (email, etc.)
	login: (params: {}) => '/login',
	register: (params: {}) => '/register',
	// TODO: add more
} as const;

/**
 * Returns correct path to NFCtron Hub app
 * @param url The URL to append to the base path or a URL builder function
 * @param stage The API stage to use, defaults to the current API stage
 * @returns The an URL object to NFCtron Hub app
 */
export const getHubUrl = (url: string | UrlBuilder = '/', stage: string | undefined = CURRENT_API_STAGE) => {
	const basePath = calcValue(() => {
		switch (stage) {
			case 'production':
				return 'https://hub.nfctron.com';
			case 'beta':
				return 'https://beta.hub.nfctron.com';
			case 'staging':
			default:
				return 'https://test.hub.nfctron.com';
		}
	});
	return typeof url === 'function' ? createUrlWithBuilder(url, basePath) : new URL(url, basePath);
};

/* -------------------------------------------------------------------------------------------------
 * Printer label URL builders
 * -----------------------------------------------------------------------------------------------*/

/**
 * Formats NFCtronLabel type URL for QR code label printing
 * @param options The options to format the URL
 * @returns The formatted NFCtronLabel URL
 */
export const getLabelPrintUrl = (options: GetLabelPrintUrlOptions): string => {
	const keys = [
		`label=${options.type}`,
		`inventoryId=${options.inventoryId}`,
		`inventoryNumber=${options.inventoryNumber}`,
		`inventoryName=${removeDiacritics(options.name)}`,
	];

	/** add deviceNumber if type is device */
	if (options.type === 'device') keys.push(`deviceNumber=${options.deviceNumber}`);
	/** add debug flag if requested */
	if (options.debug) keys.push('debug=true');

	return `NFCtronLabel:${keys.join(';')}`;
};
type GetLabelPrintUrlOptions = { debug?: boolean } & (GetLabelPrintUrlOptionsInventory | GetLabelPrintUrlOptionsDevice);
type GetLabelPrintUrlOptionsInventory = {
	type: 'inventory';
	inventoryId: UUID;
	inventoryNumber: number;
	name: string;
};
type GetLabelPrintUrlOptionsDevice = {
	type: 'device';
	inventoryId: UUID;
	inventoryNumber: number;
	name: string;
	deviceNumber: number;
};

/* -------------------------------------------------------------------------------------------------
 * Nebula Mobile deep links
 * -----------------------------------------------------------------------------------------------*/
/**
 * Nebula Mobile registered deep links parameter schemas.
 * @see https://www.notion.so/nfctron/Deeplinks-20cce0265ba24cd184f82af66497a461.
 */
const NEBULA_MOBILE_DEEP_LINKS_PARAMS = {
	chipDetail: z.object({ chipId: z.string() }),
	eventDetail: z.object({ eventId: z.string().uuid() }),
	eventGroupDetail: z.object({ eventGroupId: z.string().uuid(), codes: z.array(z.string()) }),
	orderDetail: z.object({ orderId: z.string().uuid(), orderNumber: z.number() }),
	plantOrderDetail: z.object({ plantOrderId: z.string().uuid(), orderNumber: z.number() }),
	setPassword: z.object({ accessCode: z.string(), email: z.string() }),
	login: z.object({ email: z.string() }),
	oAuth: z.object({ token: z.string(), ok: z.boolean() }),
	openUrl: z.object({ url: z.string() }),
	ticketCart: z.object({ eventId: z.string().uuid(), codes: z.array(z.string()) }),
};
export type NebulaMobileDeepLink = keyof typeof NEBULA_MOBILE_DEEP_LINKS_PARAMS;
type NebulaMobileDeepLinkParams<TDeepLink extends NebulaMobileDeepLink> = z.infer<(typeof NEBULA_MOBILE_DEEP_LINKS_PARAMS)[TDeepLink]>;

/**
 * Nebula Mobile registered deep links.
 * @see https://www.notion.so/nfctron/Deeplinks-20cce0265ba24cd184f82af66497a461.
 */
const NEBULA_MOBILE_DEEP_LINKS = {
	chipDetail: (params: NebulaMobileDeepLinkParams<'chipDetail'>) => `chipDetail?chipId=${params.chipId}`,
	eventDetail: (params: NebulaMobileDeepLinkParams<'eventDetail'>) => `eventDetail?eventId=${params.eventId}`,
	eventGroupDetail: (params: NebulaMobileDeepLinkParams<'eventGroupDetail'>) =>
		`eventGroupDetail?eventGroupId=${params.eventGroupId}${params.codes.length > 0 ? `&codes=${params.codes.join(',')}` : ''}`,
	ticketCart: (params: NebulaMobileDeepLinkParams<'ticketCart'>) =>
		`ticketCart?eventId=${params.eventId}${params.codes.length > 0 ? `&codes=${params.codes.join(',')}` : ''}`,
	orderDetail: (params: NebulaMobileDeepLinkParams<'orderDetail'>) => `orderDetail?orderId=${params.orderId}&orderNumber=${params.orderNumber}`,
	plantOrderDetail: (params: NebulaMobileDeepLinkParams<'plantOrderDetail'>) =>
		`plantOrderDetail?plantOrderId=${params.plantOrderId}&orderNumber=${params.orderNumber}`,
	setPassword: (params: NebulaMobileDeepLinkParams<'setPassword'>) => `setPassword?accessCode=${params.accessCode}&email=${params.email}`,
	login: (params: NebulaMobileDeepLinkParams<'login'>) => `login?email=${params.email}`,
	oAuth: (params: NebulaMobileDeepLinkParams<'oAuth'>) => `oAuth?token=${params.token}&ok=${params.ok}`,
	openUrl: (params: NebulaMobileDeepLinkParams<'openUrl'>) => `openUrl?url=${params.url}`,
} as const;

/**
 * Returns correct deeplink to Nebula Mobile app
 * @param deepLink The deep link to use (see registered deep links)
 * @param params The parameters to use in the deep link
 * @param stage The API stage to use, defaults to the current API stage
 * @returns The an URL object to Nebula Mobile app
 */
export const getNebulaMobileDeepLink = <TDeepLink extends NebulaMobileDeepLink>(
	deepLink: TDeepLink,
	params: NebulaMobileDeepLinkParams<TDeepLink>,
	stage: string | undefined = CURRENT_API_STAGE,
) => {
	const parsedParams = NEBULA_MOBILE_DEEP_LINKS_PARAMS[deepLink].parse(params);
	const link = NEBULA_MOBILE_DEEP_LINKS[deepLink](parsedParams as any);
	switch (stage) {
		case 'production':
			return `NFCtronNebula://${link}`;
		case 'beta':
			return `NFCtronNebulaBeta://${link}`;
		case 'staging':
		default:
			return `NFCtronNebulaDev://${link}`;
	}
};
